import { Field } from '@ynomia/core/dist/blueprint';
import { compileExpression } from 'filtrex';
import { filtrexHelpers } from '@ynomia/core/dist/utils';
import { AssetTableColumn, Layer } from '../config/types';
import { CUSTOM_DATE_COLUMNS } from '../config/constants';
import { getDisplayedLayerPath } from './layers';
import { displayPercentage } from './strings';
import {
  displayDateInProjectTime,
  formatObservationDate,
  formatReadableDateAlt,
  readableNumberOfDays,
} from './time';

/**
 * The display text for a field value, matching how `<FieldValues />` renders it in
 * the asset details pane. Returns null when the field has no textual representation
 * (photos, dividers, empty values).
 *
 * `<FieldValues />` renders through this helper, and asset search matches against
 * it, so what the details pane shows and what search matches cannot drift apart.
 */
export const getFieldDisplayText = (field: Field, value: any): string | null => {
  if (value === undefined || value === null || value === '') return null;

  switch (field.fieldType) {
    case 'text':
      return String(value);
    case 'number':
      if (field.properties?.numberFormat === 'percentage') {
        return displayPercentage(value);
      }
      return String(value);
    case 'date':
      return displayDateInProjectTime(value, 'MMM DD YYYY');
    case 'time':
    case 'datetime':
      return displayDateInProjectTime(value, 'MMM DD YYYY [at] hh:mma');
    case 'picklist': {
      if (field.properties?.multi && Array.isArray(value)) {
        return value.map(v => (
          field.properties?.options?.find((o: any) => o.value === v)?.label || 'Unknown'
        )).join(', ');
      }
      return field.properties?.options?.find((o: any) => o.value === value)?.label || 'Unknown';
    }
    case 'segmented_picklist':
      return field.properties?.columns
        ?.map(col => col?.options?.find?.(o => o.value === value?.[col.id])?.label)
        ?.filter(label => label !== undefined)
        ?.join(', ')
        || '';
    case 'checkbox':
      return value ? 'Yes' : 'No';
    case 'photo':
    case 'divider':
      return null;
    default:
      return typeof value === 'string' ? value : JSON.stringify(value);
  }
};

export type FieldShowFilter = (
  context: { values: Record<string, any>, element: Record<string, any> },
) => boolean;

/**
 * Compiles a field's filtrex `show` expression into a predicate, or null when the
 * field has none. Throws on an invalid expression, like `<FieldValues />` does.
 */
export const compileFieldShowFilter = (field: Field): FieldShowFilter | null => {
  if (!field.show) return null;
  const compiled = compileExpression(field.show, { extraFunctions: filtrexHelpers });
  return context => !!compiled(context);
};

/**
 * Whether `<FieldValues />` displays this field: not `hidden`, and its `show`
 * expression (if any) passes for the given values/element. Shared between the
 * details pane and asset search. Pass a precompiled `showFilter` when evaluating
 * many elements against the same schema.
 */
export const isFieldVisible = (
  field: Field,
  values: Record<string, any>,
  element: Record<string, any>,
  showFilter: FieldShowFilter | null = compileFieldShowFilter(field),
): boolean => {
  if (field.hidden) return false;
  if (!showFilter) return true;
  return showFilter({ values, element });
};

// Plan fields are indexed by the plan-view section, never the column loop
const PLAN_KEYS = ['planStatus', 'plannedStatus', 'plannedDueDate'];

/**
 * Cells show relative text for recent timestamps ("Today at 3:05pm"), which goes
 * stale in the cached searchable string as time passes. Index the absolute date
 * alongside it so a date search always has a stable form to match.
 */
const getDateSearchText = (value: string | Date, timezone?: string): string => {
  const relative = formatObservationDate(value, timezone);
  const absolute = formatReadableDateAlt(value, timezone);
  return relative === absolute ? relative : `${relative} ${absolute}`;
};

/**
 * The text a table cell displays for a column value, or null when the cell shows
 * nothing textual. Mirrors the render branches in getColumnFilters.
 */
const getColumnSearchText = (
  column: AssetTableColumn,
  value: any,
  timezone?: string,
): string | null => {
  if (value === undefined || value === null || value === '') return null;
  // Photo columns render as images, not text
  if (column.custom === 'observationTypePhotos') return null;
  if (CUSTOM_DATE_COLUMNS.includes(column.custom || '') || value instanceof Date) {
    return getDateSearchText(value, timezone);
  }
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  if (column.properties?.numberFormat === 'percentage') {
    return displayPercentage(value);
  }
  return String(value);
};

export type AssetSearchContext = {
  listViewColumns: Array<AssetTableColumn>;
  assetTypeFields: Array<Field>;
  fieldShowFilters: Map<string, FieldShowFilter | null>;
  layersKeyedById: Map<string, Layer>;
  timezone?: string;
  showPlanView: boolean;
};

/**
 * Precompiles everything the searchable-string builder needs that is shared
 * across assets, in particular each field's `show` predicate.
 */
export const buildAssetSearchContext = (
  listViewColumns: Array<AssetTableColumn>,
  assetTypeFields: Array<Field>,
  layersKeyedById: Map<string, Layer>,
  timezone: string | undefined,
  showPlanView: boolean,
): AssetSearchContext => ({
  listViewColumns,
  assetTypeFields,
  fieldShowFilters: new Map(assetTypeFields.map((field) => {
    // An invalid expression crashes the details pane for that asset; search
    // degrades to treating the field as visible instead of crashing the list.
    try {
      return [field.id, compileFieldShowFilter(field)];
    } catch (e) {
      return [field.id, null];
    }
  })),
  layersKeyedById,
  timezone,
  showPlanView,
});

/**
 * Builds the text the search bar matches against for one asset. Only values the
 * user can actually see are included:
 * - the visible table columns, as their cells render them
 * - the details pane's Properties section (per the field schema, as
 *   `<FieldValues />` formats them, skipping hidden and show-filtered fields)
 * - the details pane's Destination section (layer path labels and twin ID)
 * - in plan view, the plan details (PvA status, next planned status, due date),
 *   which the details pane shows whether or not they are table columns
 * Internal values (asset ids, hex colors, sort ordinals, ...) are not searchable.
 */
export const getSearchableStringFromDataSourceAsset = (
  dataSourceAsset: Record<string, any>,
  {
    listViewColumns,
    assetTypeFields,
    fieldShowFilters,
    layersKeyedById,
    timezone,
    showPlanView,
  }: AssetSearchContext,
): string => {
  const parts: Array<string> = [];

  // Always visible: asset name (details pane header) and status tag
  if (dataSourceAsset.label) parts.push(String(dataSourceAsset.label));
  if (dataSourceAsset.status) parts.push(String(dataSourceAsset.status));

  listViewColumns.forEach((column) => {
    if (column.key === 'label' || column.key === 'status') return;
    if (PLAN_KEYS.includes(column.key)) return;
    const text = getColumnSearchText(column, dataSourceAsset[column.key], timezone);
    if (text) parts.push(text);
  });

  const fieldValues = dataSourceAsset.fields || {};
  assetTypeFields.forEach((field) => {
    // The pane evaluates `show` against the raw asset; the data source row is a
    // superset of it, so the same expressions resolve identically.
    if (!isFieldVisible(
      field,
      fieldValues,
      dataSourceAsset,
      fieldShowFilters.get(field.id) ?? null,
    )) return;
    const text = getFieldDisplayText(field, fieldValues[field.id]);
    if (text) parts.push(text);
  });

  getDisplayedLayerPath(layersKeyedById, dataSourceAsset.layerID)
    .forEach(layer => parts.push(layer.label));
  if (dataSourceAsset.twinID) parts.push(String(dataSourceAsset.twinID));

  // The details pane shows plan details in plan view regardless of table columns
  if (showPlanView) {
    if (dataSourceAsset.planStatus) parts.push(String(dataSourceAsset.planStatus));
    if (dataSourceAsset.plannedStatus) parts.push(String(dataSourceAsset.plannedStatus));
    if (dataSourceAsset.plannedDueDate) {
      parts.push(getDateSearchText(dataSourceAsset.plannedDueDate, timezone));
      if (dataSourceAsset.daysUntilDueDate) {
        parts.push(`(${readableNumberOfDays(dataSourceAsset.daysUntilDueDate)})`);
      }
    }
  }

  return parts.join(' ').toLowerCase();
};
